// ── UPLOAD RECOVERY QUEUE ──
// IndexedDB-backed cache of unfinished interview uploads. When an upload
// fails (network blip, backend hiccup), the audio blob and conversation
// history live here on the storyteller's device until a retry succeeds.
// Survives page reloads and browser restarts. The retry logic in
// services/uploadRecovery.js drains the queue automatically.
//
// THREE record kinds — set the `kind` field on insert; missing/empty defaults
// to 'session' so legacy items keep working.
//
//   kind: 'session'    — full interview wrap-up. Has audioBlob (the FINAL
//                        segment if multi-segment, else the whole recording)
//                        + conversationHistory + priorSegmentKeys.
//                        Processed via /audio → /complete → /verify-audio.
//
//   kind: 'segment'    — per-segment item written when the storyteller's
//                        interview was interrupted (phone call, app switch).
//                        Has audioBlob + sessionId + segmentIndex.
//                        Processed via /audio (with segmentIndex) +
//                        /verify-audio only. NO /complete — the session is
//                        either still live or will be finalized later by its
//                        own 'session' queue item.
//
//   kind: 'checkpoint' — rolling 30-second snapshot of the in-progress
//                        segment for a still-running session. ID is
//                        deterministic ('checkpoint-' + sessionId) so the
//                        same row gets overwritten as the recording grows.
//                        On a clean session end the checkpoint is consumed
//                        (removed) and its audio merges into the session
//                        item. On an UN-clean exit (browser crash, app
//                        kill), the retry hook picks it up: uploads to
//                        /audio AND calls /complete with no conversation
//                        history — orphan-recovery path.
//
// Storage shape per record:
//   id                     unique queue id (string)
//   kind                   'session' | 'segment' | 'checkpoint'
//   audioBlob              Blob (the recorded audio)
//   conversationHistory    [{ role: 'user' | 'assistant', content: string }]
//   mode                   'interview' | 'freestyle'
//   durationSecs           number
//   storytellerAuthUserId  string | null (used by /sessions/start in proxy mode)
//   sessionId              string | null  — set after /sessions/start succeeds
//   segmentIndex           number | null  — for kind=segment/checkpoint
//   priorSegmentKeys       array | null   — keys of segments already in R2
//                                            (kind=session only)
//   audioUploaded          boolean        — set after /audio returned 200 OK
//   audioKey               string | null  — R2 key returned by /audio
//   completed              boolean        — set after /complete returned 200 OK
//   audioVerified          boolean        — set after server-side HEAD on R2
//                                            confirmed the bytes are present.
//                                            ONLY when this is true do we
//                                            delete the local blob — until
//                                            then, the queue item is the
//                                            only copy of the recording.
//   verifiedSizeBytes      number | null  — bytes R2 reports for the object
//   attempts               number         — count of retry attempts
//   lastTried              ISO string | null
//   createdAt              ISO string
//   notifiedFailure        boolean — true after the dev-team failure email fires once

const DB_NAME = 'doihaveastory_uploads';
const STORE_NAME = 'pending';
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Add a fresh upload to the queue. Defaults the bookkeeping fields so the
// retry runner can pick it up immediately.
export async function enqueueUpload(item) {
  if (!item || !item.id) throw new Error('enqueueUpload: id is required');
  const db = await openDb();
  const record = {
    kind: 'session',
    audioBlob: null,
    conversationHistory: [],
    mode: 'interview',
    durationSecs: 0,
    storytellerAuthUserId: null,
    sessionId: null,
    segmentIndex: null,
    priorSegmentKeys: null,
    audioUploaded: false,
    audioKey: null,
    completed: false,
    audioVerified: false,
    verifiedSizeBytes: null,
    attempts: 0,
    lastTried: null,
    createdAt: new Date().toISOString(),
    notifiedFailure: false,
    ...item,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(record);
    tx.oncomplete = () => resolve(record);
    tx.onerror = () => reject(tx.error);
  });
}

// Save (or update) the rolling checkpoint snapshot for an in-progress
// recording. ID is deterministic per sessionId so a put overwrites the
// previous snapshot.
export async function saveCheckpoint({ sessionId, segmentIndex, audioBlob, storytellerAuthUserId, mode, durationSecs }) {
  if (!sessionId || !audioBlob) return;
  return enqueueUpload({
    id: 'checkpoint-' + sessionId,
    kind: 'checkpoint',
    sessionId,
    segmentIndex: segmentIndex == null ? 0 : segmentIndex,
    audioBlob,
    storytellerAuthUserId: storytellerAuthUserId || null,
    mode: mode || 'interview',
    durationSecs: durationSecs || 0,
    // Reset retry counters every time we refresh the checkpoint so the
    // most recent snapshot gets a clean retry budget.
    attempts: 0,
    lastTried: null,
    notifiedFailure: false,
  });
}

export async function removeCheckpoint(sessionId) {
  if (!sessionId) return;
  return removePending('checkpoint-' + sessionId);
}

// Ask the browser not to evict our IndexedDB under storage pressure. No-op
// on browsers that don't support it. Safe to call multiple times.
export async function requestPersistentStorage() {
  try {
    if (navigator.storage && typeof navigator.storage.persist === 'function') {
      const granted = await navigator.storage.persist();
      return granted;
    }
  } catch {}
  return false;
}

export async function listPending() {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function getPending(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

// Patch fields on an existing queue item without rewriting the audio blob —
// reads, merges, writes. Idempotent for missing items.
export async function updatePending(id, patch) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (!existing) return; // tx will commit empty
      store.put({ ...existing, ...patch });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removePending(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Backoff schedule: how long to wait between retries based on attempt count.
//   0 attempts (fresh queue entry) → retry immediately
//   1 attempt → 5 seconds
//   2 → 30 seconds
//   3 → 2 minutes
//   4 → 10 minutes
//   5+ → 1 hour
export function backoffMs(attempts) {
  const schedule = [0, 5_000, 30_000, 120_000, 600_000, 3_600_000];
  return schedule[Math.min(attempts, schedule.length - 1)];
}

// True if `item` is due for a retry attempt right now.
export function isReadyToRetry(item) {
  if (!item) return false;
  if (item.completed) return false;
  if (!item.lastTried) return true;
  const since = Date.now() - new Date(item.lastTried).getTime();
  return since >= backoffMs(item.attempts);
}
